import React, { useState, useEffect } from 'react'
import './App.css'

import { AppBar, IconButton, makeStyles, Toolbar, Typography } from '@material-ui/core'

import Scale from '@tonaljs/scale'
import Chord from '@tonaljs/chord'
import Interval from '@tonaljs/interval'
import Note from '@tonaljs/note'
import ScaleType from '@tonaljs/scale-type'

import Guitar from './react-guitar'

import tunings, { Tuning } from './tunings'
import ChordList from './ChordList'

import ChordType from "@tonaljs/chord-type"

import { all } from "@tonaljs/chord-type"
import { note } from "@tonaljs/core"
import { modes } from "@tonaljs/pcset"
import ChordCategoryList from './ChordCategoryList'

import ToneSound from './ToneSound'

import { notesToChord } from './util/ToneUtils'
import { isArrayInjection } from './util/utils'
import MyAppBar from './MyAppBar'
import { allChords } from './util/TonalUtils'

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
}))

interface FoundChord {
  readonly weight: number;
  readonly name: string;
}

const namedSet = (notes: string[]) => {
  const pcToName = notes.reduce<Record<number, string>>((record, n) => {
    const chroma = note(n).chroma;
    if (chroma !== undefined) {
      record[chroma] = record[chroma] || note(n).name;
    }
    return record;
  }, {});

  return (chroma: number) => pcToName[chroma];
};

export function detect(source: string[]): string[] {
  const notes = source.map((n) => note(n).pc).filter((x) => x);
  if (note.length === 0) {
    return [];
  }

  const found: FoundChord[] = findExactMatches(notes, 1);

  console.log({ notes, found })

  return found
    .filter((chord) => chord.weight)
    .sort((a, b) => b.weight - a.weight)
    .map((chord) => chord.name);
}

function findExactMatches(notes: string[], weight: number): FoundChord[] {
  const tonic = notes[0];
  const tonicChroma = note(tonic).chroma;
  const noteName = namedSet(notes);
  // we need to test all chormas to get the correct baseNote
  const allModes = modes(notes, false);

  console.log({ tonic, tonicChroma, noteName, allModes })

  const found: FoundChord[] = [];
  allModes.forEach((mode, index) => {
    // some chords could have the same chroma but different interval spelling
    const chordTypes = all().filter((chordType) => chordType.chroma === mode);

    chordTypes.forEach((chordType) => {
      const chordName = chordType.aliases[0];
      const baseNote = noteName(index);
      const isInversion = index !== tonicChroma;
      if (isInversion) {
        found.push({
          weight: 0.5 * weight,
          name: `${baseNote}${chordName}/${tonic}`,
        });
      } else {
        found.push({ weight: 1 * weight, name: `${baseNote}${chordName}` });
      }
    });
  });

  return found;
}

const getAllArrayPermutations = (array: any[]) => {  
  let arrayAux = [...array]
  
  if (array.length <= 1) return [array]

  const arrays: any[] = []

  for (let i = 0; i < array.length; i++) {
    
    const newArrays = getAllArrayPermutations(arrayAux.slice(1))
    
    newArrays.forEach(a => arrays.push([arrayAux[0], ...a]))
    
    const firstEl = arrayAux.shift()
    arrayAux = [...arrayAux, firstEl]
  }

  return arrays
}

const initialStrings = [[], [], [], [], [], []]

type MyScaleType = {
  name: string,
  notes: string[]
}

type ChordType = {
  symbol: string,
  notes: string[]
}

const getNotesFromString = (strings: any[][], tuning: Tuning) => {
  const notes: string[] = []

  const openStringNotes = tuning.notes.split(' ').reverse()

  for (let i = 0; i < strings.length; i++) {
    
    for (let j = 0; j < strings[i].length; j++) {
      // console.log({ openString: openStringNotes[i], semitones: strings[i][j].nbr, interval: Interval.fromSemitones(strings[i][j].nbr), note: Note.transpose(openStringNotes[i], Interval.fromSemitones(strings[i][j].nbr)) })

      notes.push(Note.transpose(openStringNotes[i], Interval.fromSemitones(strings[i][j].nbr)))
    }

  }

  console.log({ notes })

  return notes.reverse()
}

const findChordsByMatchingIntervals = (chord: any) => {
  return allChords.filter(c => isArrayInjection(chord.intervals, c.intervals) || isArrayInjection(c.intervals, chord.intervals))
}

const getAllPossibleChords = (notes: string[]) => {
  let chords: any[] = []
  
  // const permutations = [notes]
  const permutations = notes.length > 6 ? [notes] : getAllArrayPermutations(notes)

  for (let i = 0; i < permutations.length; i++) {
    chords = [...chords, ...Chord.detect(permutations[i])]
  }

  chords = chords.filter((chord, i) => chords.indexOf(chord) === i)

  chords = chords.map(Chord.get).filter(c => !c.empty)
  
  return chords
}

const getAllChordsSimilar = (chords: any[]) => {
  
  let chords2: any[] = []

  for (let i = 0; i < chords.length; i++) {
    chords2 = [...chords2, ...findChordsByMatchingIntervals(chords[i]).map(c => {
      const c2 = Chord.get(chords[i].notes[0] + c.aliases[0])
      if (c2.empty) {
        console.log({ emptyChord: chords[i].notes[0] + c.aliases[0], chord: chords[i], c })
      }
      return c2
    })]
  }

  chords2 = chords2.filter(c => !c.empty)

  return chords2.filter((chord, i) => chords2.findIndex(c => c.name === chord.name) === i)
}


function ChordFinder(props: {
  settings: any,
  setTitle: (title: string) => void,
  fretsObject: any,
  constants: any,
  onSettingsChange: (settings: any) => void
}) {
  const {
    settings,
    setTitle,
    fretsObject,
    constants,
    onSettingsChange
  } = props

  const classes = useStyles()
  
  const [scale, setScale] = useState<MyScaleType | undefined>(undefined)

  const [chords, setChords] = useState<ChordType[] | undefined>(undefined)

  const [strings, setStrings] = useState<any[][]>(initialStrings)

  // Update fretboard when scale settings change (update chords too)
  useEffect(() => {
    const chords = getAllPossibleChords(getNotesFromString(strings, settings.tuning))

    const chords2 = getAllChordsSimilar(chords)
    
    console.log({ chords, chords2 })

    setChords(chords2)

  }, [strings])

  // Update fretboard
  useEffect(() => {
    setStrings(settings.tuning.notes.split(' ').map((_: any) => []))
    // setStrings(tuning.notes.length > strings.length ? [...strings, [], []] : strings.slice(strings.length - tuning.notes.length))
  }, [settings.tuning])

  return (
    <div className={classes.root}>

      <MyAppBar
        constants={constants}
        onSettingsChange={onSettingsChange}
        settings={settings}
      />

      <Guitar
        strings={strings}
        onChange={strings => setStrings(strings)}
        tuningObject={settings.tuning}
        frets={fretsObject}
        isEnharmonic
        scaleNotes={scale?.notes}
      />

      {chords && chords.length ? (
        <ChordCategoryList
          chords={chords}
          onPlayClick={(chord) => ToneSound.playNotes(notesToChord(chord.notes))}
        />
      ) : undefined}

    </div>
  )
}

export default ChordFinder

// TODO : Add select chord suggestion

// TODO : Get all scales for chord
// TODO : Click on scale suggestion redirect to scale visualizer

// TODO : Add to chord progression

// TODO : scrape chordify to have plenty of progression

// TODO : Play chord progressions
